/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Comments
export const comments = pgTable("comments", {
  id: serial("id").primaryKey(),
  activityId: integer("activity_id").notNull(),
  userId: integer("user_id").notNull(),
  text: text("text").notNull(),
  likes: integer("likes").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  parentId: integer("parent_id"), // For reply support
});

// Activity Likes
export const activityLikes = pgTable("activity_likes", {
  id: serial("id").primaryKey(),
  activityId: integer("activity_id").notNull(),
  userId: integer("user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Activity Views
export const activityViews = pgTable("activity_views", {
  id: serial("id").primaryKey(),
  activityId: integer("activity_id").notNull(),
  userId: integer("user_id").notNull(),
  viewedAt: timestamp("viewed_at").defaultNow(),
});

// Insert schemas
export const insertCommentSchema = createInsertSchema(comments).omit({ id: true, likes: true, createdAt: true });
export const insertActivityLikeSchema = createInsertSchema(activityLikes).omit({ id: true, createdAt: true });
export const insertActivityViewSchema = createInsertSchema(activityViews).omit({ id: true, viewedAt: true });

// Types
export type Comment = typeof comments.$inferSelect;
export type InsertComment = z.infer<typeof insertCommentSchema>;
export type ActivityLike = typeof activityLikes.$inferSelect;
export type InsertActivityLike = z.infer<typeof insertActivityLikeSchema>;
export type ActivityView = typeof activityViews.$inferSelect;
export type InsertActivityView = z.infer<typeof insertActivityViewSchema>; 